import { useSize } from 'ahooks'
import { Col } from 'antd'
import React, { useMemo } from 'react'
import { LayoutConfig, useQueryLayoutType } from './typing'
import { FieldsInHooksType, FieldsType } from '../../components/FieldItemList/typing'
import { useCollapsedControl } from '../useCollapsedControl'

/** 展开收起按钮  收起时候限制展示的行数 收起的时候展示两行 **/
const collapsedLimitRowIndex = 2
// 这里设了一个特殊值999999
const maxCollapsedLimitRowIndex = 99

/** 屏幕宽度对应不同的短列，必须是偶数 */
function getColNumPerLine(queryWidth: number) {
    // eslint-disable-next-line no-magic-numbers
    if (queryWidth >= 2298) {
        // eslint-disable-next-line no-magic-numbers
        return 14
        // eslint-disable-next-line no-magic-numbers
    } else if (queryWidth >= 1970) {
        // eslint-disable-next-line no-magic-numbers
        return 12
        // eslint-disable-next-line no-magic-numbers
    } else if (queryWidth >= 1642) {
        return 10
        // eslint-disable-next-line no-magic-numbers
    } else if (queryWidth >= 1314) {
        // eslint-disable-next-line no-magic-numbers
        return 8
    } else {
        // eslint-disable-next-line no-magic-numbers
        return 6
    }
}

export enum Layout {
    ROW = 'ROW', // 独占一行
    GROUP = 'GROUP', // 大列:宽度是两个 ITEM
    ITEM = 'ITEM', // 小列, 默认
    FIXED = 'FIXED'
}

export enum VIEW_TYPE {
    INLINE = 'INLINE'
}

// 获取字段分组
function getFieldsGroup({ fields, quickSearchEnable }) {
    return fields.reduce((t, c) => {
        if (!c.groupTitle) {
            return t
        }

        const group = t.find(v => v.groupTitle === c.groupTitle)

        if (group) {
            group.fields.push(c)
        } else {
            t.push({
                groupTitle: c.groupTitle,
                fields: [c]
            })
        }

        return t
    }, [])
}

export function useQueryLayout(initialFields: FieldsType[], config: LayoutConfig): useQueryLayoutType {
    const { widthRef, defaultCloNum = 0, tableNameAndId, quickSearchEnable } = config
    const width = useSize(widthRef)?.width || 0
    const { collapsed, toggleCollapsed } = useCollapsedControl(tableNameAndId)

    return useMemo<useQueryLayoutType>(() => {
        const colNumPerLine = defaultCloNum || getColNumPerLine(width)

        // 给 show field 打上 layout
        const { layoutFields, rowIndex, maxRowIndex } = calcFieldsLayout(
            initialFields,
            colNumPerLine,
            config.hasButton,
            collapsed
        )

        const fieldsGroup = getFieldsGroup({ fields: layoutFields, quickSearchEnable })
        const fieldsListGroup = [{ groupTitle: '基础筛选', fields: layoutFields }]

        return {
            groupLayout: !!fieldsGroup?.length,
            fieldsGroup: fieldsGroup?.length ? fieldsGroup : fieldsListGroup,
            colNumPerLine,
            fields: layoutFields,
            collapsed,
            toggleCollapsed,
            rowIndex,
            maxRowIndex
        }
    }, [defaultCloNum, width, initialFields, config.hasButton, collapsed, toggleCollapsed])
}

function calcFieldsLayout(initialFields: FieldsType[], colNumPerLine: number, hasButton: boolean, collapsed: boolean) {
    // 每次进来要重置状态,然后初始化一些下面要用的值
    let fields = [...initialFields].map(item => ({
        ...item,
        _collapsed: undefined,
        _hidden: item.hidden,
        _rowIndex: undefined,
        _colIndex: undefined,
        _layout: item.layout || Layout.ITEM,
        _columnSum: undefined
    })) as unknown as FieldsInHooksType[]

    // eslint-disable-next-line complexity
    const getRowIndex = (collapsed: boolean, collapsedLimitRowIndex: number, fields: FieldsInHooksType[]) => {
        let rowIndex = 0
        let columnSum = 0
        let curGroup = ''

        for (let index = 0; index < fields.length; index++) {
            const item = fields[index]

            // 初始分组
            if (!curGroup && item.groupTitle) {
                curGroup = item.groupTitle
            }

            item._layout = item.layout || Layout.ITEM

            if (collapsed && rowIndex >= collapsedLimitRowIndex) {
                item._collapsed = true
            } else {
                item._collapsed = false
            }

            item._hidden = item.hidden || item._collapsed

            // item._hidden = item?.options?.hidden || item.hidden || item._collapsed
            let colIndex = getColIndex(item, colNumPerLine)
            //
            if (!curGroup) {
                // 如果当前这个只占一格
                if ((colIndex + columnSum) % 2 === 1) {
                    // 如果是最后一个，会把搜索按钮顶到下一行,所以这里做一下特殊判断
                    if (index === fields.length - 1 && hasButton) {
                        // eslint-disable-next-line no-magic-numbers
                        if (colIndex + columnSum <= colNumPerLine - 3 && item._layout !== Layout.FIXED) {
                            item._layout = Layout.GROUP
                            colIndex = 2
                        }
                    } else {
                        // 并且下一个不是只占据一格
                        const nextItem = fields?.[index + 1] || null
                        const nextColIndex = getColIndex(nextItem, colNumPerLine)
                        if ((colIndex + columnSum + nextColIndex) % 2 === 1 && item._layout !== Layout.FIXED) {
                            // 那么就把当前这个改成 两格
                            item._layout = Layout.GROUP
                            colIndex = 2
                        }
                    }
                }
            }

            item._rowIndex = rowIndex
            item._colIndex = colIndex

            columnSum += colIndex
            item._columnSum = columnSum

            // 存在下一个字段且为新分组
            const nextItem = fields[index + 1]
            const nextGroup = nextItem && curGroup && nextItem?.groupTitle !== curGroup

            // 当前行已排满或下一项在新分组
            if (columnSum === colNumPerLine || nextGroup) {
                rowIndex += 1
                columnSum = 0
            }
        }

        return { rowIndex, fields }
    }
    const cloneWithData = fields?.map(item => ({ ...item }))

    const { fields: layoutFields, rowIndex: curRowIndex } = getRowIndex(collapsed, collapsedLimitRowIndex, fields)
    // 模拟展开时，表单的最大行
    const { rowIndex: curMaxRowIndex } = getRowIndex(false, maxCollapsedLimitRowIndex, cloneWithData)
    return {
        layoutFields,
        rowIndex: curRowIndex,
        maxRowIndex: curMaxRowIndex
    }
}

const getColIndex = (item: FieldsInHooksType, colNumPerLine: number): number => {
    if (!item) {
        return 0
    }
    if (item._hidden) {
        return 0
    }

    if (item._layout === Layout.GROUP) {
        return 2
    }

    if (item._layout === Layout.ITEM) {
        return 1
    }

    if (item._layout === Layout.ROW) {
        return colNumPerLine
    }

    return 1
}

/**
 * 这里的 show 要前置处理,这里只是兜底
 * display: !field.show ? 'none' : undefined
 * */

interface LayoutWrapType {
    colNumPerLine: number
    // todo 掉听
    field: any
    children: JSX.Element
    colStyle: any
    style: any
}
export function LayoutWrap(props: LayoutWrapType) {
    const { field, children, colNumPerLine, colStyle, style } = props

    if (colNumPerLine === 0 || field._hidden) {
        return children
    }

    const width = field._layout === Layout.FIXED ? field.colWidth : calcColWith(field._layout, colNumPerLine)

    return (
        <Col
            style={{
                width,
                ...(colStyle || {}),
                ...(style || {})
            }}
        >
            {children}
        </Col>
    )
}

function calcColWith(layout: Layout = Layout.ITEM, colNumPerLine: number) {
    const item = 100 / colNumPerLine

    if (layout === Layout.ROW) {
        return '100%'
    } else if (layout === Layout.GROUP) {
        return `${item * 2}%`
    } else {
        return `${item}%`
    }
}
